import { Button, Checkbox, Input, Radio, Header, ModalContent, ModalActions, Modal, Popup } from 'semantic-ui-react';
import { jsx, css } from '@emotion/core';
import { space } from 'styled-system';
import React from 'react';
import styled from '@emotion/styled';
import shouldForwardProp from '@styled-system/should-forward-prop';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var veracodeColors = {
  black: '#000000',
  white: '#ffffff',
  trueBlack: '#000000',
  fierceFuchsia: '#d92b85',
  roboticRed: '#d92b85',
  orneryOrange: '#fd7333',
  tipOffTangerine: '#f19425',
  sassySaffron: '#fbb627',
  leapingLemon: '#ffcc33',
  livingLime: '#c9da2c',
  giganticGreen: '#8dbd3e',
  bebeBlue: '#94ceda',
  steelTeal: '#00a0ac',
  blue: '#0aa2dc',
  irateIris: '#4500bc',
  galacticGrape: '#8800b8',
  sharkBlack: '#242626',
  deepCerulean: '#007da3',
  cerulean: '#00b3e6',
  foam: '#f7fcfe',
  chenin: '#e2e367',
  buttons: {
    blue: '#1b4677',
    darkBlue: '#0d223b',
    lightBlue: '#c6d1dd',
    lightFuschia: '#dac5d0'
  },
  modals: {
    innerBorders: '#e5e5e5',
    text: '#333333'
  }
};

var baseButtonStyles = {
  borderRadius: '3px',
  borderStyle: 'solid',
  borderWidth: '1px',
  cursor: 'pointer',
  display: 'inline-block',
  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  letterSpacing: '0.02em',
  lineHeight: 1,
  '&:hover,&:active,&:focus': {
    textDecoration: 'none'
  },
  '&:disabled': {
    opacity: 1,
    pointerEvents: 'none'
  }
};
var buttonVariants = {
  primary: {
    color: veracodeColors.white,
    backgroundColor: veracodeColors.buttons.blue,
    borderColor: veracodeColors.buttons.blue,
    '&:hover,&:active,&:focus': {
      backgroundColor: veracodeColors.buttons.darkBlue,
      borderColor: veracodeColors.buttons.darkBlue,
      color: veracodeColors.white
    },
    '&:disabled': {
      backgroundColor: veracodeColors.buttons.lightBlue,
      borderColor: veracodeColors.buttons.lightBlue,
      color: '#ecf0f3'
    }
  },
  secondary: {
    color: veracodeColors.buttons.blue,
    backgroundColor: veracodeColors.white,
    borderColor: veracodeColors.buttons.blue,
    '&:hover,&:active,&:focus': {
      backgroundColor: '#edf1f5',
      borderColor: veracodeColors.buttons.blue,
      color: veracodeColors.buttons.blue
    },
    '&:disabled': {
      backgroundColor: veracodeColors.white,
      borderColor: veracodeColors.buttons.lightBlue,
      color: veracodeColors.buttons.lightBlue
    }
  },
  tertiary: {
    color: veracodeColors.buttons.blue,
    backgroundColor: veracodeColors.white,
    borderColor: '#dbdbdb',
    '&:hover,&:active,&:focus': {
      backgroundColor: '#f7f7f7',
      borderColor: '#cccccc',
      color: veracodeColors.buttons.blue
    },
    '&:disabled': {
      backgroundColor: veracodeColors.white,
      borderColor: '#e8e8e8',
      color: veracodeColors.buttons.lightBlue
    }
  },
  urgentPrimary: {
    color: veracodeColors.white,
    backgroundColor: veracodeColors.fierceFuchsia,
    borderColor: veracodeColors.fierceFuchsia,
    '&:hover,&:active,&:focus': {
      backgroundColor: '#6d1643',
      borderColor: '#6d1643',
      color: veracodeColors.white
    },
    '&:disabled': {
      backgroundColor: veracodeColors.buttons.lightFuschia,
      borderColor: veracodeColors.buttons.lightFuschia,
      color: '#f5f0f3'
    }
  },
  urgentSecondary: {
    color: veracodeColors.fierceFuchsia,
    backgroundColor: veracodeColors.white,
    borderColor: veracodeColors.fierceFuchsia,
    '&:hover,&:active,&:focus': {
      backgroundColor: '#fdf4f9',
      borderColor: veracodeColors.fierceFuchsia,
      color: veracodeColors.fierceFuchsia
    },
    '&:disabled': {
      backgroundColor: veracodeColors.white,
      borderColor: veracodeColors.buttons.lightFuschia,
      color: veracodeColors.buttons.lightFuschia
    }
  }
};
var buttonSizes = {
  small: {
    fontSize: '12px',
    padding: '6px 9px'
  },
  medium: {
    fontSize: '13px',
    padding: '8px 11px'
  },
  large: {
    fontSize: '18px',
    padding: '12px 14px',
    fontWeight: 700
  }
};

var getButtonStyles = function getButtonStyles(_ref, props) {
  var variant = _ref.variant,
      size = _ref.size;
  return css([baseButtonStyles, buttonSizes[size || 'small'], buttonVariants[variant || 'secondary'], space(props)]);
};

var VCButton = function VCButton(props) {
  var variant = props.variant,
      size = props.size,
      rest = _objectWithoutPropertiesLoose(props, ["variant", "size"]);

  return jsx(Button, Object.assign({}, rest, {
    css: getButtonStyles({
      variant: variant,
      size: size
    }, props)
  }));
};

var baseCheckboxStyles =
/*#__PURE__*/
css({
  display: 'block',
  textColor: '#333',
  fontFamily: '"Helvetica", Helvetica, sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  input: {
    margin: '5px'
  }
});

var VCCheckboxButton = function VCCheckboxButton(props) {
  return React.createElement(Checkbox, Object.assign({}, props));
};

var VCCheckboxButtonWithStyles =
/*#__PURE__*/
styled(VCCheckboxButton)(baseCheckboxStyles);

var baseInputStyles = {
  '&.ui.input > input': {
    borderRadius: '4px',
    borderStyle: 'solid',
    borderWidth: '1px',
    padding: '2px 4px',
    borderColor: '#ccc',
    height: '24px !important',
    backgroundColor: veracodeColors.white,
    fontFamily: '"Helvetica", Helvetica, sans-serif',
    fontSize: '13px'
  }
};
var smallInput = {
  '&.ui.input > input': {
    width: '200px'
  }
};
var largeInput = {
  '&.ui.input > input': {
    width: '460px'
  }
};

var getInputStyles = function getInputStyles(size) {
  switch (size) {
    case 'small':
      return css([baseInputStyles, smallInput]);

    case 'large':
      return css([baseInputStyles, largeInput]);

    default:
      return css([baseInputStyles, smallInput]);
  }
};

var VCInput = function VCInput(props) {
  var size = props.size,
      rest = _objectWithoutPropertiesLoose(props, ["size"]);

  return jsx(Input, Object.assign({
    css: getInputStyles(size)
  }, rest));
};

/** @jsx jsx */
var baseRadioButtonStyles =
/*#__PURE__*/
css({
  display: 'block',
  textColor: '#333',
  fontFamily: '"Helvetica", Helvetica, sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  input: {
    margin: '5px'
  }
});
var VCRadioButton = function VCRadioButton(props) {
  return jsx(Radio, Object.assign({}, props, {
    css: baseRadioButtonStyles
  }));
};

/** @jsx jsx */
var baseSelectStyles =
/*#__PURE__*/
css({
  display: 'block',
  fontFamily: 'Helvetica, sans-serif',
  fontSize: '13px',
  borderColor: '#ccc',
  textColor: '#333',
  lineHeight: 'normal',
  fontWeight: 300,
  width: '15.2rem',
  height: '24px',
  marginTop: '1em',
  border: '1px solid #ccc'
});
var VCSelect = function VCSelect(props) {
  return jsx("select", Object.assign({}, props, {
    css: baseSelectStyles
  }));
};

var Form = function Form() {
  return React.createElement(React.Fragment, null);
};

Form.Checkbox = VCCheckboxButtonWithStyles;
Form.Input = VCInput;
Form.Radio = VCRadioButton;
Form.Select = VCSelect;

var baseHeaderStyles =
/*#__PURE__*/
css({
  fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
  textColor: veracodeColors.trueBlack,
  lineHeight: 'normal',
  fontWeight: 600,
  margin: 0
});
var header1 =
/*#__PURE__*/
css({
  fontSize: '25px'
});
var header2 =
/*#__PURE__*/
css({
  fontSize: '16px'
});
var header3 =
/*#__PURE__*/
css({
  fontSize: '13px'
});

var headerStyle = function headerStyle(props) {
  switch (props.as) {
    case 'h1':
      return header1;

    case 'h2':
      return header2;

    case 'h3':
      return header3;

    case 'h4':
      return header3;

    case 'h5':
      return header3;

    case 'h6':
      return header3;

    default:
      return header1;
  }
};

var VCHeaders = function VCHeaders(props) {
  return React.createElement(Header, Object.assign({}, props));
};
var VCHeaderWithStyle =
/*#__PURE__*/
styled(VCHeaders, {
  shouldForwardProp: shouldForwardProp
})(baseHeaderStyles, headerStyle, space);

var modalContentStyles =
/*#__PURE__*/
css({
  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  color: veracodeColors.modals.text,
  fontWeight: 'normal',
  display: 'block',
  fontSize: '14px',
  lineHeight: 'normal',
  padding: '20px 17px 0 17px',
  paddingBottom: 0,
  overflowY: 'auto',
  flex: '1 1 auto'
});

var VCModalContent = function VCModalContent(props) {
  return React.createElement(ModalContent, Object.assign({}, props));
};

var VCModalContentWithStyles =
/*#__PURE__*/
styled(VCModalContent, {
  shouldForwardProp: shouldForwardProp
})(modalContentStyles);

var modalActionsStyles =
/*#__PURE__*/
css({
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: veracodeColors.modals.innerBorders,
  display: 'flex',
  textAlign: 'center',
  justifyContent: 'center',
  padding: '1.2rem',
  boxSizing: 'border-box',
  flex: '0 0 auto'
});

var VCModalActions = function VCModalActions(props) {
  return React.createElement(ModalActions, Object.assign({}, props));
};

var VCModalActionsWithStyles =
/*#__PURE__*/
styled(VCModalActions, {
  shouldForwardProp: shouldForwardProp
})(modalActionsStyles);

var modalHeaderStyles =
/*#__PURE__*/
css({
  fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
  color: veracodeColors.trueBlack,
  lineHeight: 'normal',
  fontWeight: 'bold',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: veracodeColors.modals.innerBorders,
  fontSize: '16px',
  padding: '0.94rem',
  marginBottom: 0,
  flex: '0 0 auto'
});

var VCModalHeader = function VCModalHeader(props) {
  return React.createElement(Header, Object.assign({}, props));
};

var VCModalHeaderWithStyles =
/*#__PURE__*/
styled(VCModalHeader, {
  shouldForwardProp: shouldForwardProp
})(modalHeaderStyles);

/** @jsx jsx */
var modalStyles =
/*#__PURE__*/
css({
  '&.ui.modal': {
    backgroundColor: veracodeColors.white,
    borderRadius: '0.375rem',
    bottom: 'initial',
    boxShadow: '0 0.19rem 0.56rem rgba(0, 0, 0, 0.5), 0 0.19rem 0.56rem rgba(0, 0, 0, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    left: 'initial',
    minHeight: '200px',
    maxHeight: '80vh',
    position: 'absolute',
    right: 'initial',
    top: 'initial',
    transformOrigin: '50% 25%',
    userSelect: 'text',
    willChange: 'top, left, margin, transform, opacity',
    'z-index': '1001 !important'
  },
  '&.ui.modal.tiny': {
    width: '350px'
  },
  '&.ui.modal.small': {
    width: '500px'
  },
  '&.ui.modal.large': {
    width: '750px'
  },
  '.close.icon': {
    fontStyle: 'normal',
    fontWeight: 'bold'
  },
  '.close.icon::after': {
    content: '"×"',
    fontSize: '1.31rem',
    color: veracodeColors.trueBlack,
    opacity: 0.2,
    display: 'block',
    position: 'absolute',
    right: 0,
    padding: '0.8rem 1rem',
    cursor: 'pointer'
  },
  '.close.icon:hover::after': {
    opacity: 1
  }
});

var getModalStyles = function getModalStyles() {
  return css([modalStyles]);
};

var VCModal = function VCModal(props) {
  /* Veracode Modals always have the 'inverted' dimmer aka the 'Milkout' */
  return jsx(Modal, Object.assign({}, props, {
    dimmer: "inverted",
    css: getModalStyles()
  }));
};
VCModal.Header = VCModalHeaderWithStyles;
VCModal.Content = VCModalContentWithStyles;
VCModal.Actions = VCModalActionsWithStyles;

var ConfirmModal = function ConfirmModal(props) {
  return React.createElement(VCModal, Object.assign({
    size: "tiny"
  }, props));
};

var ComplexModal = function ComplexModal(props) {
  return React.createElement(VCModal, Object.assign({
    size: "large"
  }, props));
};

var SimpleModal = function SimpleModal(props) {
  return React.createElement(VCModal, Object.assign({
    size: "small"
  }, props));
};

var baseTooltipStyles =
/*#__PURE__*/
css({
  '&.ui.popup': {
    display: 'none',
    position: 'absolute',
    top: 0,
    right: 0,
    margin: '0em',
    minWidth: 'min-content',
    maxWidth: '5.625rem',
    zIndex: 1900,
    border: '1px solid #D4D4D5',
    borderRadius: '0.25rem',
    minHeight: '1.25rem',
    background: veracodeColors.white,
    padding: '0.54rem',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 'normal',
    fontSize: '13px',
    fontStyle: 'normal',
    textAlign: 'center',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: '0px 2px 4px 0px rgba(34, 36, 38, 0.12), 0px 2px 10px 0px rgba(34, 36, 38, 0.15)'
  },
  '&.ui.popup:before': {
    position: 'absolute',
    content: '""',
    width: '0.71428571em',
    height: '0.71428571em',
    background: '#FFFFFF',
    transform: 'rotate(45deg)',
    zIndex: 2,
    boxShadow: '1px 1px 0px 0px #bababc'
  },

  /*--------------
   Spacing
  ---------------*/

  /* Extending from Top */
  '&.ui.top.popup': {
    margin: '0em 0em 0.71428571em'
  },
  '&.ui.top.center.popup': {
    transformOrigin: 'center bottom'
  },

  /* Extending from Vertical Center */
  '&.ui.left.center.popup': {
    margin: '0em 0.71428571em 0em 0em',
    transformOrigin: 'right 50%'
  },
  '&.ui.right.center.popup': {
    margin: '0em 0em 0em 0.71428571em',
    transformOrigin: 'left 50%'
  },

  /* Extending from Bottom */
  '&.ui.bottom.popup': {
    margin: '0.71428571em 0em 0em'
  },
  '&.ui.bottom.center.popup': {
    transformOrigin: 'center top'
  },
  '&.ui.popup > .content': {
    paddingBottom: 'initial'
  },

  /*--------------
     Pointer
  ---------------*/

  /* --- Below ---*/
  '&.ui.bottom.center.popup:before': {
    marginLeft: '-0.30714286em',
    top: '-0.30714286em',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    boxShadow: '-1px -1px 0px 0px #bababc'
  },
  '&.ui.bottom.left.popup': {
    marginLeft: '0em',
    transformOrigin: 'left top'
  },
  '&.ui.bottom.left.popup:before': {
    top: '-0.30714286em',
    left: '1em',
    right: 'auto',
    bottom: 'auto',
    marginLeft: '0em',
    boxShadow: '-1px -1px 0px 0px #bababc'
  },
  '&.ui.bottom.right.popup': {
    marginRight: '0em',
    transformOrigin: 'right top'
  },
  '&.ui.bottom.right.popup:before': {
    top: '-0.30714286em',
    right: '1em',
    bottom: 'auto',
    left: 'auto',
    marginKeft: '0em',
    boxShadow: '-1px -1px 0px 0px #bababc'
  },

  /* --- Above ---*/
  '&.ui.top.center.popup:before': {
    top: 'auto',
    right: 'auto',
    bottom: '-0.30714286em',
    left: '50%',
    marginLeft: '-0.30714286em'
  },
  '&.ui.top.left.popup': {
    marginLeft: '0em',
    transformOrigin: 'left bottom'
  },
  '&.ui.top.left.popup:before': {
    bottom: '-0.30714286em',
    left: '1em',
    top: 'auto',
    right: 'auto',
    marginLeft: '0em'
  },
  '&.ui.top.right.popup': {
    marginRight: '0em',
    transformOrigin: 'right bottom'
  },
  '&.ui.top.right.popup:before': {
    bottom: '-0.30714286em',
    right: '1em',
    top: 'auto',
    left: 'auto',
    marginLeft: '0em'
  },

  /* --- Left Center ---*/
  '&.ui.left.center.popup:before': {
    top: '50%',
    right: '-0.30714286em',
    bottom: 'auto',
    left: 'auto',
    marginTop: '-0.30714286em',
    boxShadow: '1px -1px 0px 0px #bababc'
  },

  /* --- Right Center  ---*/
  '&.ui.right.center.popup:before': {
    top: '50%',
    left: '-0.30714286em',
    bottom: 'auto',
    right: 'auto',
    marginTop: '-0.30714286em',
    boxShadow: '-1px 1px 0px 0px #bababc'
  },

  /* Arrow Color By Location */
  '&.ui.bottom.popup:before': {
    background: '#FFFFFF'
  },
  '&.ui.right.center.popup:before,&.ui.left.center.popup:before': {
    background: '#FFFFFF'
  },
  '&.ui.top.popup:before': {
    background: '#FFFFFF'
  },

  /* Inverted Arrow Color */
  '&.ui.inverted.bottom.popup:before': {
    background: '#191919'
  },
  '&.ui.inverted.right.center.popup:before,&.ui.inverted.left.center.popup:before': {
    background: '#191919'
  },
  '&.ui.inverted.top.popup:before': {
    background: '#191919'
  },
  '&.ui.loading.popup': {
    display: 'block',
    visibility: 'hidden',
    'z-index': '-1'
  },
  '&.ui.animating.popup,&.ui.visible.popup': {
    display: 'block'
  },
  '&.ui.visible.popup': {
    transform: 'translateZ(0px)',
    backfaceVisibility: 'hidden'
  },

  /*--------------
     Basic
  ---------------*/
  '&.ui.basic.popup:before': {
    display: 'none'
  },

  /*--------------
     Wide
  ---------------*/
  '&.ui.wide.popup': {
    maxWidth: '350px'
  },
  '&.ui[class*="very wide"].popup': {
    maxWidth: '550px'
  },

  /*--------------
     Fluid
  ---------------*/
  '&.ui.flowing.popup': {
    maxWidth: 'none'
  },

  /*--------------
     Colors
  ---------------*/

  /* Inverted colors  */
  '&.ui.inverted.popup': {
    background: '#191919',
    color: veracodeColors.white,
    border: 'none',
    boxShadow: 'none'
  },
  '&.ui.inverted.popup .header': {
    backgroundColor: 'none',
    color: veracodeColors.white
  },
  '&.ui.inverted.popup:before': {
    backgroundColor: veracodeColors.trueBlack,
    boxShadow: 'none !important'
  }
});

var getTooltipStyles = function getTooltipStyles(props) {
  return css([baseTooltipStyles, space(props)]);
};

var getSUIPopupPosition = function getSUIPopupPosition(position) {
  if (position === 'top') {
    return 'top center';
  }

  if (position === 'right') {
    return 'right center';
  }

  if (position === 'bottom') {
    return 'bottom center';
  }

  if (position === 'left') {
    return 'left center';
  }

  return position;
};

var VCTooltip = function VCTooltip(props) {
  var position = props.position,
      rest = _objectWithoutPropertiesLoose(props, ["position"]);

  return jsx(Popup, Object.assign({
    inverted: true,
    position: getSUIPopupPosition(position),
    css: getTooltipStyles
  }, rest));
};

export { VCButton as Button, ComplexModal, ConfirmModal, Form, VCHeaderWithStyle as Header, VCModal as Modal, SimpleModal, VCTooltip as Tooltip };
//# sourceMappingURL=fluorine-ui.esm.js.map
